/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
'use strict';

import { Badge, Button, Spinner } from 'react-bootstrap';
import { DiffChange, PendingChange } from '../data';

function opBadge(op: string) {
    if (op === 'add') return <Badge bg="success">Add</Badge>;
    if (op === 'delete') return <Badge bg="danger">Delete</Badge>;
    return <Badge bg="warning" text="dark">Change</Badge>;
}

function typeBadge(type: string) {
    if (type === 'category') return <Badge bg="info">Cat</Badge>;
    return <Badge bg="secondary">FP</Badge>;
}

export function changeKey(c: DiffChange): string {
    if (c.type === 'fingerprint') return c.fp?.pvkey || c.before?.pvkey || '';
    return c.category?.key || c.before?.key || '';
}

export function changeTitle(c: DiffChange): string {
    if (c.type === 'fingerprint') return c.fp?.title || c.fp?.pvkey || c.before?.title || c.before?.pvkey || '';
    return c.category?.title || c.category?.key || c.before?.title || c.before?.key || '';
}

export function changeToPendingChange(c: DiffChange): PendingChange {
    const isCat = c.type === 'category';
    const after = isCat ? (c.op === 'delete' ? null : c.category) : (c.op === 'delete' ? null : c.fp);
    const before = c.before ?? null;
    return {
        id: 0,
        proposal_key: '',
        proposal_type: c.op === 'add' ? 'add' : c.op === 'delete' ? 'delete' : 'update',
        fingerprint_id: null,
        target_fingerprint_id: null,
        title: changeTitle(c),
        summary: '',
        severity: c.op === 'delete' ? 'danger' : 'info',
        status: 'todo',
        detail: isCat
            ? { categoryKey: c.category?.key || c.before?.key, before, after }
            : { pvkey: c.fp?.pvkey || c.before?.pvkey, before, after, matches: c.fp?.matches || before?.matches || [] },
        created: 0,
        updated: 0,
    };
}

type DiffChangesTableProps = {
    changes: DiffChange[];
    onOpen: (change: PendingChange) => void;
    onHoverChange?: (change: PendingChange | null) => void;
    showRevert?: boolean;
    revertingKey?: string | null;
    revertingAll?: boolean;
    onRevert?: (change: DiffChange) => void;
};

function DiffChangesTable({
    changes,
    onOpen,
    onHoverChange,
    showRevert = false,
    revertingKey = null,
    revertingAll = false,
    onRevert,
}: DiffChangesTableProps) {
    return (
        <table className="table table-sm table-borderless mb-2" style={{ tableLayout: 'fixed' }}>
            <colgroup>
                <col style={{ width: '3.5rem' }} />
                <col style={{ width: '4.5rem' }} />
                <col style={{ width: '220px' }} />
                <col />
                <col style={{ width: '5.5rem' }} />
            </colgroup>
            <thead>
                <tr className="border-bottom">
                    <th>Type</th>
                    <th>Op</th>
                    <th>Key</th>
                    <th>Title / Name</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {changes.map((c, i) => {
                    const key = changeKey(c);
                    const id = `${c.type}:${key}`;
                    const isReverting = revertingKey === id;
                    const pending = changeToPendingChange(c);
                    return (
                        <tr
                            key={i}
                            style={{ cursor: 'pointer' }}
                            onClick={() => { onHoverChange?.(null); onOpen(pending); }}
                        >
                            <td>{typeBadge(c.type)}</td>
                            <td
                                onMouseEnter={() => onHoverChange?.(pending)}
                                onMouseLeave={() => onHoverChange?.(null)}
                                onClick={e => { e.stopPropagation(); onOpen(pending); }}
                                style={{ cursor: 'default' }}
                            >
                                {opBadge(c.op)}
                            </td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{key}</td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{changeTitle(c)}</td>
                            <td onClick={e => e.stopPropagation()}>
                                {showRevert ? (
                                    <Button
                                        size="sm"
                                        variant="outline-warning"
                                        disabled={isReverting || revertingAll}
                                        onClick={() => onRevert?.(c)}
                                    >
                                        {isReverting ? <Spinner size="sm" animation="border" /> : 'Revert'}
                                    </Button>
                                ) : null}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

export default DiffChangesTable;
